import { useState, useEffect, useCallback } from 'react'

const fmtR = (val) => {
  if (val === null || val === undefined || val === '') return '-'
  return `R${Number(val).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const fmtDate = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d)) return val
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
}

const fmtTime = (val) => {
  if (!val) return '-'
  const d = new Date(val)
  if (isNaN(d)) return '-'
  return d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
}

export function ImportBatchesTab({ fetchBatches, deleteBatches, clearAllTransactions, isDarkMode }) {
  const [batches, setBatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(new Set())
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmClearAll, setConfirmClearAll] = useState(false)
  const [clearingAll, setClearingAll] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchBatches()
      setBatches(data)
    } finally {
      setLoading(false)
    }
  }, [fetchBatches])

  useEffect(() => { load() }, [load])

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelected(prev => prev.size === batches.length ? new Set() : new Set(batches.map(b => b.id)))
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await deleteBatches(Array.from(selected))
      setSelected(new Set())
      setConfirmDelete(false)
      await load()
    } finally {
      setDeleting(false)
    }
  }

  const handleClearAll = async () => {
    setClearingAll(true)
    try {
      await clearAllTransactions()
      setSelected(new Set())
      setConfirmClearAll(false)
      await load()
    } finally {
      setClearingAll(false)
    }
  }

  // Totals row
  const totals = batches.reduce((acc, b) => ({
    count: acc.count + (b.transaction_count || 0),
    supplierExcl: acc.supplierExcl + (b.total_supplier_excl || 0),
    advisorExcl: acc.advisorExcl + (b.total_advisor_excl || 0),
    advisorInc: acc.advisorInc + (b.total_advisor_inc || 0),
  }), { count: 0, supplierExcl: 0, advisorExcl: 0, advisorInc: 0 })

  const th = `text-xs font-semibold uppercase tracking-wide px-3 py-2 text-left ${isDarkMode ? 'text-slate-400 bg-slate-900' : 'text-gray-500 bg-gray-50'}`
  const td = `px-3 py-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`
  const trBase = `border-b transition-colors ${isDarkMode ? 'border-slate-700 hover:bg-slate-700/40' : 'border-gray-100 hover:bg-gray-50'}`

  return (
    <div className="flex flex-col gap-3">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>
          {batches.length} import batch{batches.length !== 1 ? 'es' : ''}
        </span>
        {selected.size > 0 && (
          <button
            onClick={() => setConfirmDelete(true)}
            className="px-3 py-1.5 rounded text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition"
          >
            Delete {selected.size} batch{selected.size !== 1 ? 'es' : ''}
          </button>
        )}
        <button
          onClick={load}
          className={`ml-auto px-3 py-1.5 rounded text-sm transition ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Table */}
      <div className={`overflow-hidden rounded border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="overflow-auto">
          <table className="w-full min-w-max">
            <thead>
              <tr>
                <th className={`${th} w-8`}>
                  <input
                    type="checkbox"
                    checked={batches.length > 0 && selected.size === batches.length}
                    onChange={toggleAll}
                    className="cursor-pointer"
                  />
                </th>
                <th className={th}>Import #</th>
                <th className={th}>Import Date</th>
                <th className={th}>Time</th>
                <th className={th}>Transactions</th>
                <th className={th}>Supplier Total (Excl)</th>
                <th className={th}>Advisor Total (Excl)</th>
                <th className={th}>Advisor Total (Inc)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className={`px-3 py-8 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    Loading batches...
                  </td>
                </tr>
              ) : batches.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`px-3 py-8 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    No import batches yet. Import an Excel file to see batches here.
                  </td>
                </tr>
              ) : (
                batches.map(b => (
                  <tr key={b.id} className={trBase}>
                    <td className={`${td} w-8`}>
                      <input
                        type="checkbox"
                        checked={selected.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className={td}>
                      <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-white text-sfp-orange' : 'bg-sfp-orange/10 text-sfp-orange'}`}>
                        #{b.sequence_number}
                      </span>
                    </td>
                    <td className={td}>{fmtDate(b.import_date)}</td>
                    <td className={`${td} text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>{fmtTime(b.imported_at)}</td>
                    <td className={`${td} font-semibold text-sfp-orange`}>{b.transaction_count}</td>
                    <td className={td}>{fmtR(b.total_supplier_excl)}</td>
                    <td className={td}>{fmtR(b.total_advisor_excl)}</td>
                    <td className={`${td} font-semibold`}>{fmtR(b.total_advisor_inc)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {batches.length > 1 && (
              <tfoot>
                <tr className={`border-t-2 ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-gray-300 bg-gray-50'}`}>
                  <td colSpan={4} className={`${td} font-bold`}>Totals ({batches.length} batches)</td>
                  <td className={`${td} font-bold text-sfp-orange`}>{totals.count}</td>
                  <td className={`${td} font-bold`}>{fmtR(totals.supplierExcl)}</td>
                  <td className={`${td} font-bold`}>{fmtR(totals.advisorExcl)}</td>
                  <td className={`${td} font-bold`}>{fmtR(totals.advisorInc)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'}`}>
              Delete {selected.size} Batch{selected.size !== 1 ? 'es' : ''}?
            </h3>
            <p className={`text-sm mb-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              All transactions in the selected batch{selected.size !== 1 ? 'es' : ''} will be <strong>permanently deleted</strong>. You can reimport them afterwards.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
              >
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm Clear All Modal */}
      {confirmClearAll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-sm w-full mx-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-2 text-red-600`}>
              🗑 Clear All Transactions?
            </h3>
            <p className={`text-sm mb-2 ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              This will <strong>permanently delete every transaction and all import batch records</strong>. You can then do a clean reimport.
            </p>
            <p className={`text-xs mb-5 font-semibold text-red-500`}>
              ⚠️ This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmClearAll(false)}
                className="btn-secondary"
                disabled={clearingAll}
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                disabled={clearingAll}
                className="px-4 py-2 rounded bg-red-700 hover:bg-red-800 text-white text-sm font-semibold transition"
              >
                {clearingAll ? 'Clearing...' : 'Yes, Clear All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
